import { useState, type ReactNode } from 'react';
import { View, Text, Image, ScrollView, useWindowDimensions, Platform } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useRouter } from 'expo-router';
import { Menu, X, ChevronLeft } from 'lucide-react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useThemeColors } from '@/lib/useThemeColors';
import { LOGO_WORDMARK_WHITE } from '@/lib/logo';

/** Dark beta color palette */
const C = {
  bgDeep: '#060e1a',
  bgPrimary: '#0a1628',
  bgElevated: '#0f1f38',
  teal: '#4fd1c5',
  white: '#FFFFFF',
  text1: 'rgba(255,255,255,0.85)',
  text2: 'rgba(255,255,255,0.6)',
  text3: 'rgba(255,255,255,0.38)',
  surface: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.15)',
};

const isWeb = Platform.OS === 'web';

interface PublicLayoutProps {
  children: ReactNode;
  /** Page title shown in mobile header */
  title: string;
  /** Optional icon shown next to the title on mobile */
  titleIcon?: ReactNode;
  /** Max width for content area on web (default: 800) */
  maxWidth?: number;
  /** Whether to wrap children in a centered content container (default: true).
   *  Set to false for pages that handle their own max-width (e.g. pricing with wider cards). */
  wrapContent?: boolean;
}

/**
 * Shared layout for all public/marketing pages.
 * - Web: landing-page-style nav bar + content + footer
 * - Mobile: SafeAreaView + back-button header + content
 */
export function PublicLayout({
  children,
  title,
  titleIcon,
  maxWidth = 800,
  wrapContent = true,
}: PublicLayoutProps) {
  const colors = useThemeColors();

  if (isWeb) {
    return (
      <View className="flex-1" style={{ backgroundColor: C.bgPrimary }}>
        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
          <PublicNavBar />
          <View style={{ minHeight: 400 }}>
            {wrapContent ? (
              <View
                className="px-5 pb-8 pt-10"
                style={{ maxWidth, alignSelf: 'center', width: '100%' }}
              >
                {children}
              </View>
            ) : (
              children
            )}
          </View>
          <PublicFooter />
        </ScrollView>
      </View>
    );
  }

  // Mobile native: back-button header pattern (unchanged)
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top']} className="flex-1">
        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
          <MobileHeader title={title} titleIcon={titleIcon} />
          <View className="px-5 pb-8">
            {children}
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}

/** Navigation bar — dark glassmorphic style matching beta landing page */
export function PublicNavBar() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View
      style={{
        backgroundColor: 'rgba(6,14,26,0.88)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottomWidth: 1,
        borderBottomColor: C.border,
        paddingHorizontal: isMobile ? 16 : 32,
        paddingVertical: 16,
      } as any}
    >
      <View className="flex-row items-center justify-between">
        <AnimatedPressable onPress={() => router.push('/')} className="flex-row items-center gap-3">
          <Image
            source={LOGO_WORDMARK_WHITE}
            style={{ width: 160, height: 52 }}
            resizeMode="contain"
          />
        </AnimatedPressable>

        {isMobile ? (
          <AnimatedPressable onPress={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? (
              <X size={24} color={C.text1} />
            ) : (
              <Menu size={24} color={C.text1} />
            )}
          </AnimatedPressable>
        ) : (
          <View className="flex-row items-center gap-6">
            <NavLink label="About" onPress={() => router.push('/about')} />
            <NavLink label="Help" onPress={() => router.push('/help')} />
          </View>
        )}
      </View>

      {/* Mobile menu dropdown */}
      {isMobile && menuOpen && (
        <View className="mt-4 gap-2 pb-2">
          <MobileNavLink label="About" onPress={() => { setMenuOpen(false); router.push('/about'); }} />
          <MobileNavLink label="Help & Support" onPress={() => { setMenuOpen(false); router.push('/help'); }} />
        </View>
      )}
    </View>
  );
}

function NavLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <AnimatedPressable onPress={onPress}>
      <Text style={{ color: C.text2 }} className="font-medium">{label}</Text>
    </AnimatedPressable>
  );
}

function MobileNavLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <AnimatedPressable
      onPress={onPress}
      className="py-3 px-4 rounded-lg"
      style={{ backgroundColor: C.surface }}
    >
      <Text style={{ color: C.text1 }} className="font-medium">{label}</Text>
    </AnimatedPressable>
  );
}

/** Four-column footer matching the beta dark landing page */
export function PublicFooter() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  return (
    <View style={{ backgroundColor: C.bgDeep, borderTopWidth: 1, borderTopColor: C.border, paddingHorizontal: isMobile ? 20 : 32, paddingTop: isMobile ? 32 : 56, paddingBottom: isMobile ? 24 : 32 }}>
      <View style={{ maxWidth: 1100, alignSelf: 'center', width: '100%' }}>
        {/* Footer Grid */}
        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 32 : 0, marginBottom: isMobile ? 32 : 40 }}>
          {/* Brand Column */}
          <View style={{ flex: isMobile ? undefined : 1.4, marginRight: isMobile ? 0 : 32 }}>
            <View className="flex-row items-center gap-3 mb-3">
              <Image
                source={LOGO_WORDMARK_WHITE}
                style={{ width: 120, height: 40 }}
                resizeMode="contain"
              />
            </View>
            <Text style={{ color: C.text3, fontSize: 13, lineHeight: 20, maxWidth: 280 }}>
              AI-powered investment readiness scoring for startups and investors. Built in Switzerland.
            </Text>
          </View>

          {/* Company Column */}
          <View style={{ flex: isMobile ? undefined : 1 }}>
            <Text style={{ color: C.text3, fontSize: 12, fontWeight: '600', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              Company
            </Text>
            <FooterLink label="About" onPress={() => router.push('/about')} />
            <FooterLink label="Contact" onPress={() => router.push('/contact')} />
            <FooterLink label="Help" onPress={() => router.push('/help')} />
          </View>

          {/* Legal Column */}
          <View style={{ flex: isMobile ? undefined : 1 }}>
            <Text style={{ color: C.text3, fontSize: 12, fontWeight: '600', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              Legal
            </Text>
            <FooterLink label="Terms of Service" onPress={() => router.push('/terms')} />
            <FooterLink label="Privacy Policy" onPress={() => router.push('/privacy')} />
            <FooterLink label="Cookie Policy" onPress={() => router.push('/cookies')} />
            <FooterLink label="Refund Policy" onPress={() => router.push('/refunds')} />
            <FooterLink label="Disclaimer" onPress={() => router.push('/disclaimer')} />
            <FooterLink label="AI Disclosure" onPress={() => router.push('/ai-disclosure')} />
          </View>
        </View>

        {/* Bottom Bar */}
        <View style={{ borderTopWidth: 1, borderTopColor: C.border, paddingTop: 16, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? 6 : 0 }}>
          <Text style={{ color: C.text3, fontSize: 13 }}>
            © 2026 Inverio. All rights reserved.
          </Text>
          <Text style={{ color: C.text3, fontSize: 13 }}>
            support@inverio.ai
          </Text>
        </View>
      </View>
    </View>
  );
}

function FooterLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <AnimatedPressable onPress={onPress} style={{ paddingVertical: 5 }}>
      <Text style={{ color: C.text2, fontSize: 14 }}>{label}</Text>
    </AnimatedPressable>
  );
}

/** Simple back-button header for mobile */
function MobileHeader({ title, titleIcon }: { title: string; titleIcon?: ReactNode }) {
  const colors = useThemeColors();
  const router = useRouter();

  return (
    <View className="flex-row items-center gap-4 px-5 pt-4 pb-6">
      {/* Public pages are opened from emails and deep links, where there is
          no history to pop: router.back() alone would be a dead control
          (CLAUDE.md navigation_and_inset_ownership §3). Fall back to the root. */}
      <AnimatedPressable
        onPress={() => (router.canGoBack() ? router.back() : router.replace('/'))}
        accessibilityRole="button"
        accessibilityLabel="Back"
        className="w-10 h-10 rounded-full items-center justify-center shrink-0"
        style={{ backgroundColor: colors.surface.primary }}
      >
        <ChevronLeft size={24} color={colors.text.primary} />
      </AnimatedPressable>
      <View className="flex-1 flex-row items-center gap-2">
        {titleIcon}
        <Text numberOfLines={1} style={{ color: colors.text.primary }} className="flex-1 text-2xl font-bold">
          {title}
        </Text>
      </View>
    </View>
  );
}
